/* global LINKED_APPLICATIONS_EVENTAPP */
import { useQueryClient } from '@tanstack/react-query';
import { Input, Table, Typography, Space, Divider, Select, Tooltip } from 'antd';
import moment from 'moment';
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { t } from 'ttag';

import { DatePicker } from 'common/components/DatePicker';
import Empty from 'common/components/Empty';
import ShowMore from 'common/components/ShowMore';
import useVisibleColumns from 'common/components/Table/useVisibleColumns';
import useSyncWithUrlSearchParams from 'common/hooks/useSyncWithUrlSearchParams';
import { useTableQuery } from 'common/hooks/useTableQuery';

import EmptyImage from '@assets/images/illustrations/events.svg';

import FeatureFlagsModal from './FeatureFlags';
import { featureFlagsQueryOptions } from './FeatureFlags/queries';
import MoveEventModal from './MoveEvent';
import { EventSearchQueryKey } from './queries';

const { Search } = Input;

const SearchBox = styled(Search)`
  && {
    width: 350px;
  }
`;

const Events = () => {
  const [query, setQuery] = useSyncWithUrlSearchParams();
  const [searchBy, setSearchBy] = useState(query.searchBy || 'shortcode');
  const [search, setSearch] = useState(query.search || '');
  const [startDateRange, setStartDateRange] = useState(() => {
    const { startDateMin, startDateMax } = query;
    return startDateMin && startDateMax ? [startDateMin, startDateMax] : null;
  });
  const [targetEventToMove, setTargetEventToMove] = useState(null);
  const [targetEventForFeatureFlags, setTargetEventForFeatureFlags] = useState(null);

  const sortOrder = name => {
    const hasSort = query.sort?.includes(name) ?? false;

    if (!hasSort) {
      return false;
    }

    return query.sort.startsWith('-') ? 'descend' : 'ascend';
  };

  const queryClient = useQueryClient();
  const prefetchFeatureFlags = async shortcode => {
    queryClient.prefetchQuery({
      ...featureFlagsQueryOptions(shortcode),
    });
  };

  // Sync state changes to URL
  useEffect(() => {
    setQuery(prev => ({
      ...prev,
      searchBy,
      startDateMin: startDateRange?.[0],
      startDateMax: startDateRange?.[1],
    }));
  }, [searchBy, startDateRange, setQuery]);

  const columns = useVisibleColumns(
    [
      // Event Detail Columns
      {
        key: 'id',
        dataIndex: 'id',
        title: t`ID`,
        sorter: true,
        sortOrder: sortOrder('id'),
        width: 100,
      },
      {
        key: 'shortcode',
        dataIndex: 'shortcode',
        title: t`Code`,
        sorter: true,
        sortOrder: sortOrder('shortcode'),
        width: 120,
        render: shortcode => (
          <Typography.Text code style={{ wordBreak: 'keep-all' }}>
            {shortcode}
          </Typography.Text>
        ),
      },
      {
        key: 'name',
        dataIndex: 'name',
        title: t`Name`,
        sorter: true,
        sortOrder: sortOrder('name'),
        width: 250,
      },
      {
        key: 'creationDate',
        dataIndex: 'creationDate',
        title: t`Created at`,
        sorter: true,
        sortOrder: sortOrder('creationDate'),
        width: 130,
      },
      {
        key: 'startDate',
        dataIndex: 'startDate',
        title: t`Start Date`,
        sorter: true,
        sortOrder: sortOrder('startDate'),
        width: 130,
      },
      {
        key: 'endDate',
        dataIndex: 'endDate',
        title: t`End Date`,
        sorter: true,
        sortOrder: sortOrder('endDate'),
        width: 130,
      },
      {
        key: 'organizerEmails',
        dataIndex: 'users',
        title: t`Organizer Emails`,
        width: 200,
        render: users => (
          <ShowMore
            isTag
            uppercase={false}
            items={users?.map(user => ({ id: user.id, name: user.email })) || []}
          />
        ),
      },
      {
        key: 'package',
        dataIndex: ['package', 'name'],
        title: t`Package`,
        width: 150,
        render: (_, event) => event.package?.name || '-',
      },

      // Actions Column
      {
        key: 'actions',
        title: t`Actions`,
        width: 300,
        render: (_, event) => (
          <Space separator={<Divider orientation="vertical" />}>
            <Tooltip title={t`Event App`}>
              <Typography.Link
                target="_blank"
                rel="noopener noreferrer"
                href={`https://${LINKED_APPLICATIONS_EVENTAPP}/${event.shortcode}`}
              >
                EA
              </Typography.Link>
            </Tooltip>
            <Tooltip title={t`Experience`}>
              <Link to={`/organization/${event.organizationId}/event/${event.id}`}>EXP</Link>
            </Tooltip>
            <Typography.Link onClick={() => setTargetEventToMove(event)}>{t`Move`}</Typography.Link>
            <Typography.Link
              onMouseEnter={() => prefetchFeatureFlags(event.shortcode)}
              onClick={() => setTargetEventForFeatureFlags(event)}
            >
              {t`Feature flags`}
            </Typography.Link>
          </Space>
        ),
      },
    ],
    {
      defaultVisibleColumns: ['id', 'shortcode', 'name', 'creationDate', 'startDate', 'endDate'],
      omitColumns: ['actions'],
      disabledColumns: ['id', 'shortcode', 'name'],
    },
  );

  const {
    tableProps,
    query: { refetch },
  } = useTableQuery(
    apiParams => ({
      queryKey: EventSearchQueryKey.getQueryKey(apiParams, searchBy, startDateRange),
      queryFn: EventSearchQueryKey.queryFn,
    }),
    {
      columns,
      rowKey: 'id',
    },
    search,
  );

  return (
    <>
      <Space style={{ width: '100%', justifyContent: 'flex-end', marginBlock: 20 }}>
        <DatePicker.RangePicker
          presets={[
            { label: t`All Events`, value: null },
            { label: t`Ongoing Events`, value: [moment(), moment()] },
            {
              label: t`Upcoming Events`,
              value: [moment().add(1, 'day'), moment().add(4, 'week')],
            },
          ]}
          placeholder={[t`Min Start Date`, t`Max Start Date`]}
          value={
            startDateRange ? [moment(startDateRange[0]), moment(startDateRange[1])] : undefined
          }
          onChange={(_, dateRangeStr) => setStartDateRange(dateRangeStr?.[0] ? dateRangeStr : null)}
        />
        <Space.Compact>
          <Select
            value={searchBy}
            onChange={value => setSearchBy(value)}
            style={{ textAlign: 'left', width: 150 }}
            options={[
              { value: 'id', label: t`Event ID` },
              { value: 'name', label: t`Event Name` },
              { value: 'shortcode', label: t`Event Code` },
              { value: 'organizer', label: t`Organizer Email` },
            ]}
          />
          <SearchBox
            defaultValue={search}
            placeholder={t`Search Events`}
            disabled={tableProps.loading}
            onSearch={value => setSearch(value)}
          />
        </Space.Compact>
      </Space>

      {!search && (
        <Empty image={EmptyImage} title={t`Search for events by name, code, or organizer email.`} />
      )}
      {search && <Table {...tableProps} scroll={{ x: 'max-content' }} />}

      {targetEventToMove && (
        <MoveEventModal
          event={targetEventToMove}
          onClose={async success => {
            if (success) {
              await refetch();
            }
            setTargetEventToMove(null);
          }}
        />
      )}

      {targetEventForFeatureFlags && (
        <FeatureFlagsModal
          event={targetEventForFeatureFlags}
          onClose={() => setTargetEventForFeatureFlags(null)}
        />
      )}
    </>
  );
};

export default Events;
